import PropTypes from "prop-types";
import Link from "next/link";
import { useEffect, useState } from "react";
import Iconify from "../iconify";
import { useRouter } from "next/router";
import CustomIconButton from "@/src/lib/ui/iconButton";
import CustomChip from "@/src/lib/ui/customChip";
import useBreakpoint from "@/src/lib/util/custom-hooks/useBreakPoint";

export default function TestHeaderInfo({
    testDetails,
    testVariantDetails,
    selectedTest,
}) {
    const router = useRouter();
    const { configProductCard } = testDetails || {};
    const { categoryType, category, categorySlug } = configProductCard || {};
    const [showMore, setShowMore] = useState(false);

    // Media query for mobile view
    const isMobile = useBreakpoint("sm");

    // Construct subtitle using a cleaner conditional (switch or mapping)
    const categoryMap = {
        lab: "",
        scan: "Scan | ",
        packages: "Health Package | ",
    };

    const pathName =
        categoryType === "packages"
            ? "/health-packages"
            : `/facilities/${categorySlug}`;

    const subTittle = `${
        categoryMap[categoryType] || "Other Test | "
    }${category}`;

    // Extract other values
    const testName = testVariantDetails?.itemName;
    const total = selectedTest?.rate;
    const mrp = selectedTest?.mrp;
    const keywords = testVariantDetails?.aliases;
    const description = testDetails?.bodyHtml;

    useEffect(() => {
        if (description) {
            document.querySelector(".test-description").innerHTML =
                isMobile && !showMore
                    ? `${description.slice(0, 200)}...`
                    : description;
        }
    }, [description, isMobile, showMore]);

    const toggleShowMore = () => setShowMore(!showMore);

    const handleBack = () => {
        router.back();
    };

    return (
        <section className="rounded-xl border border-slate-200 bg-white p-5 md:p-7 shadow-sm">
            {/* Row 1: Back, Category, Heading, MRP and Price */}
            {!isMobile && (
                <div className="flex items-start justify-between gap-6">
                    <div className="flex min-w-0 items-start gap-3">
                        <CustomIconButton
                            onClick={handleBack}
                            aria-label="Go back"
                            className="mt-0.5 shrink-0 border border-solid border-slate-200 text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                        >
                            <Iconify
                                width={18}
                                icon={
                                    "material-symbols:arrow-back-ios-new-rounded"
                                }
                            />
                        </CustomIconButton>
                        <div className="min-w-0">
                            <Link
                                href={{
                                    pathname: pathName,
                                }}
                                title={subTittle}
                                className="inline-flex text-xs font-semibold uppercase tracking-wider text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-sm"
                            >
                                {subTittle}
                            </Link>
                            <h1 className="mt-1.5 text-2xl lg:text-[28px] font-bold leading-tight tracking-tight text-slate-900">
                                {testDetails?.productName}
                            </h1>
                        </div>
                    </div>

                    {!mrp && !total ? null : (
                        <div className="shrink-0 text-right">
                            <p className="text-sm leading-5 text-slate-400 line-through">
                                ₹{mrp}
                            </p>
                            <p className="text-2xl lg:text-[28px] font-bold leading-tight text-slate-900">
                                ₹{total}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Row 2: Also Known As */}
            {keywords?.length > 0 && (
                <div
                    className={`flex flex-wrap items-center gap-2 ${
                        !isMobile ? "mt-6 border-t border-slate-100 pt-5" : ""
                    }`}
                >
                    <p className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Also known as
                    </p>
                    {keywords?.map((keyword, index) => (
                        <CustomChip
                            size={isMobile ? "small" : "medium"}
                            key={index}
                            label={keyword}
                            variant="outlined"
                            color="default"
                            className="cursor-default! border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-50"
                        />
                    ))}
                </div>
            )}

            {/* Row 3: Description */}
            <div className="mt-4 text-sm md:text-[15px] leading-7 text-slate-600">
                <p className="test-description text-slate-600 leading-7 [&_p]:text-slate-600 [&_p]:leading-7 [&_span]:text-slate-600" />
                {isMobile && (
                    <button
                        type="button"
                        onClick={toggleShowMore}
                        className="mt-2 inline-flex items-center gap-1 rounded-sm text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                        aria-expanded={showMore}
                        aria-label={
                            showMore
                                ? "Collapse description"
                                : "Expand description"
                        }
                    >
                        {showMore ? "Read less" : "Read more"}
                        <Iconify
                            width={16}
                            icon="material-symbols:keyboard-arrow-down-rounded"
                            className={`text-primary transition-transform duration-200 ${
                                showMore ? "rotate-180" : ""
                            }`}
                        />
                    </button>
                )}
            </div>
        </section>
    );
}

TestHeaderInfo.propTypes = {
    testName: PropTypes.string,
    subTittle: PropTypes.string,
    total: PropTypes.number,
    mrp: PropTypes.number,
    icon: PropTypes.element,
    color: PropTypes.string,
    keywords: PropTypes.array,
    description: PropTypes.string,
};
